'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useMemo, useState } from 'react';

import Fuse from 'fuse.js';
import PropTypes from 'prop-types';

import CardFoodServiceProduct from 'components/cards/CardFoodServiceProduct';
import CardNoResults from 'components/cards/CardNoResults';
import FilterBody from 'components/ui/FilterBody/';

import useQueryState from 'hooks/useQueryState';
import { blockRootProps } from 'utils';

import styles from './BlockFoodServiceProductList.module.scss';

const BlockFoodServiceProductList = ({
    __typename,
    anchorId,
    products: dirtyProducts,
    theme,
    headline,
    copy,
    useCategorySort = false,
    searchParams,
}) => {
    theme ??= 'foodService';
    searchParams ??= {};

    const [state, setState] = useQueryState(searchParams);

    const [search, setSearch] = useState('');

    const products = useMemo(() => {
        const ids = [];
        const dedupedProducts = dirtyProducts.filter(product => {
            if (!product?.sys?.id) {
                return false;
            }

            if (ids.includes(product.sys.id)) {
                console.warn(`Duplicate product "${product.displayName}"`);
                return false;
            }

            ids.push(product.sys.id);
            return true;
        });

        return useCategorySort
            ? dedupedProducts.sort((a, b) => {
                  return a.order - b.order;
              })
            : dedupedProducts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirtyProducts]);

    const searcher = useMemo(() => {
        return new Fuse(products, {
            keys: ['displayName', 'pageTitle', 'searchKeywords'],
            shouldSort: false,
        });
    }, [products]);

    // Run though all products to build a list of available filters
    const filters = useMemo(() => {
        return products.reduce((o, product) => {
            Object.entries(product.attributes || {}).forEach(([key, value]) => {
                const categoryAttribute =
                    product.category.attributes[key] || false;

                if (!categoryAttribute) {
                    return;
                }

                const options = categoryAttribute.options.filter(filter => {
                    if (categoryAttribute.type === 'multiple') {
                        return [...value].includes(`${filter.id}`);
                    } else {
                        return `${filter.id}` === `${value}`;
                    }
                });

                if (options.length === 0) {
                    console.warn(
                        `Category attribute value of \`${value}\` not found attribute in ${key}: ${product.displayName}`
                    );
                    return;
                }

                const optionObj = options.reduce((obj, item) => {
                    obj[item.id] = item;
                    return obj;
                }, {});

                o[key] = {
                    ...categoryAttribute,
                    options: {
                        ...((o[key] && o[key].options) || {}),
                        ...optionObj,
                    },
                };
            });

            return o;
        }, {});
    }, [products]);

    const [filtered, setFiltered] = useState(state);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearch = useCallback(evt => {
        setSearch(evt.target.value);
    });

    const handleClear = () => {
        setSearch('');
        setFiltered({});
        setState({});
    };

    const productsSearched =
        search.length === 0
            ? products
            : searcher.search(search).map(product => product.item);

    const productsFiltered = productsSearched.filter(product => {
        if (Object.keys(state).length === 0) {
            return true;
        }
        return Object.keys(state).every(attrKey => {
            if (!product.attributes) {
                return false;
            }
            const option = product.category?.attributes[attrKey]?.options.find(
                option => option.slug == state[attrKey]
            );
            if (Array.isArray(product.attributes[attrKey])) {
                return product.attributes[attrKey].includes(`${option?.id}`);
            }
            return `${product.attributes[attrKey]}` === `${option?.id}`;
        });
    });

    const handleChangeFilter = key => event => {
        const state = {
            ...filtered,
            [key]: event.currentTarget.value,
        };
        if (event.currentTarget.value === '') {
            delete state[key];
        }
        setState(state);
        setFiltered(state);
    };

    const isNoResults = productsFiltered.length === 0;

    return (
        <div {...blockRootProps(anchorId, __typename)}>
            <FilterBody
                onSearch={handleSearch}
                onClear={handleClear}
                theme={theme}
                headline={headline}
                copy={copy}
            >
                {Object.keys(filters).map(key => {
                    return Object.keys(filters[key].options).length >= 2 ? (
                        <FilterBody.Select
                            key={`${key}-select`}
                            filters={filters}
                            filtered={filtered}
                            keyValue={key}
                            label={filters[key].label}
                            value={filtered[key] || ''}
                            onChange={handleChangeFilter(key)}
                        />
                    ) : null;
                })}
            </FilterBody>
            <ul className={styles.productList}>
                {productsFiltered.map(product => {
                    if (!product.category || !product.slug) {
                        // These are an absolute must
                        return null;
                    }
                    return (
                        <li className={styles.listItem} key={product.sys.id}>
                            <CardFoodServiceProduct
                                color={product.color}
                                category={product.category}
                                eyebrow={product.eyebrow}
                                image={product.cardImage}
                                displayName={product.displayName}
                                slug={product.slug}
                                theme={product.theme}
                                award={product.award}
                            />
                        </li>
                    );
                })}
                <li key="end-card" className={styles.listItem}>
                    {isNoResults && <CardNoResults search={search} />}
                </li>
            </ul>
        </div>
    );
};

BlockFoodServiceProductList.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    products: PropTypes.array,
    theme: PropTypes.string,
    headline: PropTypes.string,
    copy: PropTypes.object,
    useCategorySort: PropTypes.bool,
    contentTypeId: PropTypes.string,
    searchParams: PropTypes.object,
};

export default BlockFoodServiceProductList;
